<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { call, identity, type Schemas } from '../api/http'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatDate } from '../i18n'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t } = useI18n()
const router = useRouter()
const session = useSession()
const load = useForm()
const action = useForm()
const invite = useForm()

const members = ref<Schemas['Member'][]>([])
const invitations = ref<Schemas['Invitation'][]>([])
const lastLink = ref('')
const copied = ref(false)
const form = reactive({ email: '', role: 'member' as Schemas['InvitableRole'] })

const wid = computed(() => session.workspace?.id ?? '')
const path = computed(() => ({ path: { workspace_id: wid.value } }))
const isOwner = computed(() => session.workspace?.role === 'owner')
const roles = computed<Schemas['Role'][]>(() => (isOwner.value ? ['owner', 'admin', 'member', 'viewer'] : ['admin', 'member', 'viewer']))

async function reload() {
  if (!wid.value) return
  await load.submit(async () => {
    const [m, inv] = await Promise.all([
      call(identity.GET('/workspaces/{workspace_id}/members', { params: path.value })),
      session.canAdmin ? call(identity.GET('/workspaces/{workspace_id}/invitations', { params: path.value })) : [],
    ])
    members.value = m
    invitations.value = inv
  })
}
watch(wid, reload, { immediate: true })

// Owners can change anyone; admins cannot touch owners. The server enforces the same rules.
const canEdit = (m: Schemas['Member']) => session.canAdmin && m.user_id !== session.user?.id && (isOwner.value || m.role !== 'owner')

async function changeRole(m: Schemas['Member'], role: Schemas['Role']) {
  const ok = await action.submit(async () => {
    const updated = await call(
      identity.PATCH('/workspaces/{workspace_id}/members/{user_id}', {
        params: { path: { workspace_id: wid.value, user_id: m.user_id } },
        body: { role },
      }),
    )
    Object.assign(m, updated)
  })
  if (!ok) {
    // restore the select to the server state; keep the error visible
    const err = action.error.value
    await reload()
    action.error.value = err
  }
}

async function removeMember(m: Schemas['Member']) {
  const self = m.user_id === session.user?.id
  if (!confirm(self ? t('members.leaveConfirm') : t('members.removeConfirm', { name: m.display_name }))) return
  const ok = await action.submit(() =>
    call(identity.DELETE('/workspaces/{workspace_id}/members/{user_id}', { params: { path: { workspace_id: wid.value, user_id: m.user_id } } })),
  )
  if (!ok) return
  if (self) {
    // identity moved this session to the personal workspace; pick that up
    await session.refresh()
    await session.loadMe()
    router.push('/')
  } else members.value = members.value.filter((x) => x.user_id !== m.user_id)
}

async function onInvite() {
  copied.value = false
  const ok = await invite.submit(async () => {
    const inv = await call(identity.POST('/workspaces/{workspace_id}/invitations', { params: path.value, body: { ...form } }))
    lastLink.value = `${location.origin}/invite/${inv.token_once}`
    invitations.value = [{ ...inv, token_once: undefined }, ...invitations.value]
  })
  if (ok) form.email = ''
}

async function copyLink() {
  await navigator.clipboard?.writeText(lastLink.value)
  copied.value = true
}

const revokeInvitation = (inv: Schemas['Invitation']) =>
  action.submit(async () => {
    await call(
      identity.DELETE('/workspaces/{workspace_id}/invitations/{invitation_id}', {
        params: { path: { workspace_id: wid.value, invitation_id: inv.id } },
      }),
    )
    invitations.value = invitations.value.filter((x) => x.id !== inv.id)
  })
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>{{ $t('members.title') }}</h1>
        <p class="muted">{{ $t('members.subtitle', { workspace: session.workspace?.name }) }}</p>
      </div>
    </header>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <UiCard v-if="session.canAdmin" :title="$t('members.inviteTitle')" :subtitle="$t('members.inviteSubtitle')">
      <form class="invite" data-testid="invite-form" @submit.prevent="onInvite">
        <UiField :label="$t('fields.email')" :error="invite.fieldErr('email')">
          <input v-model.trim="form.email" name="email" type="email" required :aria-invalid="!!invite.fieldErr('email')" />
        </UiField>
        <UiField :label="$t('fields.role')" :error="invite.fieldErr('role')">
          <select v-model="form.role" name="role">
            <option v-for="r in ['admin', 'member', 'viewer'] as const" :key="r" :value="r">{{ $t(`roles.${r}`) }}</option>
          </select>
        </UiField>
        <UiButton type="submit" :loading="invite.busy.value">{{ $t('members.inviteAction') }}</UiButton>
      </form>
      <div class="stack" style="margin-top: 1rem; gap: 0.5rem">
        <ErrorBanner :error="invite.error.value" />
        <div v-if="lastLink" class="link-box">
          <p class="small">{{ $t('members.linkReady') }}</p>
          <div class="row">
            <span class="mono" data-testid="invite-link">{{ lastLink }}</span>
            <UiButton size="sm" variant="outline" @click="copyLink">{{ copied ? $t('common.copied') : $t('common.copy') }}</UiButton>
          </div>
        </div>
      </div>
    </UiCard>

    <UiCard :title="$t('members.listTitle')">
      <p v-if="load.busy.value && !members.length" class="muted">{{ $t('common.loading') }}</p>
      <ul v-else class="list" data-testid="member-list">
        <li v-for="m in members" :key="m.user_id">
          <div class="stack" style="gap: 0.1rem; flex: 1; min-width: 12rem">
            <strong>
              {{ m.display_name }}
              <span v-if="m.user_id === session.user?.id" class="muted small">({{ $t('members.you') }})</span>
            </strong>
            <span class="muted small">{{ m.email }} · {{ $t('members.joined', { date: formatDate(m.joined_at) }) }}</span>
          </div>
          <select
            v-if="canEdit(m)"
            class="role-select"
            :value="m.role"
            :aria-label="$t('fields.role')"
            :disabled="action.busy.value"
            @change="changeRole(m, ($event.target as HTMLSelectElement).value as Schemas['Role'])"
          >
            <option v-for="r in roles" :key="r" :value="r">{{ $t(`roles.${r}`) }}</option>
          </select>
          <UiBadge v-else variant="outline">{{ $t(`roles.${m.role}`) }}</UiBadge>
          <UiButton v-if="canEdit(m)" size="sm" variant="ghost" :disabled="action.busy.value" @click="removeMember(m)">
            {{ $t('members.remove') }}
          </UiButton>
          <UiButton v-else-if="m.user_id === session.user?.id" size="sm" variant="ghost" :disabled="action.busy.value" @click="removeMember(m)">
            {{ $t('members.leave') }}
          </UiButton>
        </li>
      </ul>
    </UiCard>

    <UiCard v-if="session.canAdmin" :title="$t('members.pendingTitle')">
      <EmptyState v-if="!invitations.length" :title="$t('members.noPending')" :message="$t('members.noPendingText')" />
      <ul v-else class="list">
        <li v-for="inv in invitations" :key="inv.id">
          <div class="stack" style="gap: 0.1rem; flex: 1; min-width: 12rem">
            <strong>{{ inv.email }}</strong>
            <span class="muted small">{{ $t('members.expires', { date: formatDate(inv.expires_at) }) }}</span>
          </div>
          <UiBadge variant="outline">{{ $t(`roles.${inv.role}`) }}</UiBadge>
          <UiButton size="sm" variant="ghost" :disabled="action.busy.value" @click="revokeInvitation(inv)">{{ $t('members.revoke') }}</UiButton>
        </li>
      </ul>
    </UiCard>
  </div>
</template>

<style scoped>
.invite {
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: 0.75rem;
  align-items: end;
}
@media (max-width: 640px) {
  .invite {
    grid-template-columns: 1fr;
  }
}
.role-select {
  width: auto;
  min-height: 2rem;
  padding: 0.2rem 0.5rem;
}
.link-box {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 0.75rem;
  background: var(--bg);
}
</style>
